import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { TemplateScope } from '../../../extViewmodels/sdk/models/SurveyTemplate';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

export interface ICreateSurveyTemplateParams {
	name: string;
	summary: string;
	keywords?: string[];
	surveyDefinition: Api.ICustomSurvey;
	scope?: TemplateScope;
	enabled?: boolean;
}

export interface ICreateSurveyTemplateOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ISurveyTemplate) => void;
}

/**
 * Create a new survey template via the admin API.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useCreateSurveyTemplateMutation = (options: ICreateSurveyTemplateOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: ICreateSurveyTemplateParams) => {
			const template: Partial<Api.ISurveyTemplate> = {
				name: params.name,
				summary: params.summary,
				keywords: params.keywords || [],
				surveyDefinition: params.surveyDefinition,
				scope: params.scope ?? TemplateScope.System,
				enabled: params.enabled ?? true,
			};

			return userSession.webServiceHelper.callAsync<Api.ISurveyTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'admin/surveytemplate',
				}),
				'POST',
				template
			);
		},
		onSuccess: data => {
			// Invalidate relevant queries
			reactQueryClient.invalidateQueries({ queryKey: ['systemSurveyTemplate'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplatesByIndustry'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplate', 'browse'] });
			onSuccess?.(data);
		},
		onError,
	});
};
